import { AppIcon } from '@/components/ui/app-icon';
import {
  REVIEW_REWARD_CREDITS,
  ReviewRewardDialog,
} from '@/components/review-reward-dialog.component';
import { iconArrowRight, iconStar, iconXmark } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useGetMyReviewCreditQuery } from '@/store/features/review-credits.feature';
import { useUser } from '@clerk/react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { isDismissedWithin, stampDismissal } from '@/lib/timed-dismissal';

/** AppSumo staff shouldn't be prompted to review their own marketplace. */
function isAppsumoStaffEmail(emails: string[]): boolean {
  return emails.some((e) => e.toLowerCase().trim().endsWith('@appsumo.com'));
}

/** Per-user dismissal, TTL-limited so a still-unclaimed reward resurfaces. */
const DISMISS_KEY_PREFIX = 'cl_review_reward_dismissed_';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Full-width bar above the page card offering AppSumo orgs credits for a
 * review.
 *
 * Operationally gated on the same PostHog flag as the backend
 * (`require_feature_flag('review_credits_v1')`), which is org-keyed to AppSumo
 * orgs — so this only renders where the reward actually applies. Hidden once the
 * reward is approved; a compact confirmation shows while a submission is pending.
 */
export function ReviewRewardBanner() {
  const { user } = useUser();
  const userId = user?.id;
  const enabled = useFeatureFlagEnabled('review_credits_v1') === true;
  const { data: myReview } = useGetMyReviewCreditQuery(undefined, {
    skip: !enabled,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (userId)
      setDismissed(
        isDismissedWithin(`${DISMISS_KEY_PREFIX}${userId}`, DISMISS_TTL_MS)
      );
  }, [userId]);

  const claimed = myReview?.status === 'approved';
  const pending = myReview?.status === 'pending';
  const isAppsumoStaff = isAppsumoStaffEmail(
    (user?.emailAddresses ?? []).map((e) => e.emailAddress)
  );

  // Nothing to offer: flag off, AppSumo staff, already rewarded, or dismissed.
  if (!enabled || isAppsumoStaff || claimed || dismissed) return null;

  const handleDismiss = () => {
    if (userId) stampDismissal(`${DISMISS_KEY_PREFIX}${userId}`);
    setDismissed(true);
  };

  return (
    <>
      <div
        role='region'
        aria-label='AppSumo review reward'
        className={cn(
          'flex h-10 items-center gap-3 border-b px-4',
          'border-amber-200 bg-amber-50',
          'dark:border-amber-500/20 dark:bg-amber-500/10'
        )}
      >
        <AppIcon
          icon={iconStar}
          className='h-4 w-4 shrink-0 text-amber-500'
          style={{ fontSize: 14 }}
        />

        {pending ? (
          <p className='truncate text-[13px] text-amber-800 dark:text-amber-200'>
            <span className='font-semibold'>Review submitted</span>
            <span className='hidden sm:inline'>
              {' '}
              — we'll add {REVIEW_REWARD_CREDITS.toLocaleString()} credits once
              it's approved.
            </span>
          </p>
        ) : (
          <p className='truncate text-[13px] text-amber-800 dark:text-amber-200'>
            <span className='font-semibold'>
              Get {REVIEW_REWARD_CREDITS.toLocaleString()} free credits
            </span>
            <span className='hidden sm:inline'>
              {' '}
              — leave us a review on AppSumo.
            </span>
          </p>
        )}

        <div className='ml-auto flex shrink-0 items-center gap-2'>
          {!pending && (
            <button
              type='button'
              onClick={() => setDialogOpen(true)}
              className='inline-flex h-7 items-center gap-1 rounded-md bg-amber-500 px-3 text-xs font-semibold text-white shadow-xs transition-colors hover:bg-amber-500/90 focus-visible:ring-1 focus-visible:ring-amber-500/60 focus-visible:outline-hidden'
            >
              Leave a review
              <AppIcon icon={iconArrowRight} className='h-3 w-3' />
            </button>
          )}
          <button
            type='button'
            onClick={handleDismiss}
            aria-label='Dismiss review reward banner'
            className='rounded-md p-1 text-amber-700/70 transition-colors hover:bg-amber-500/15 hover:text-amber-800 focus-visible:ring-1 focus-visible:ring-amber-500/60 focus-visible:outline-hidden dark:text-amber-300/70 dark:hover:text-amber-200'
          >
            <AppIcon icon={iconXmark} className='h-3.5 w-3.5' />
          </button>
        </div>
      </div>

      <ReviewRewardDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  );
}
